<template>
  <div class="charging-point-firmware">
    <v-card outlined class="trigger" :class="{'trigger--attention': firmware && firmware.advice.state === 'update-available'}" @click="open = true">
      <v-card-text class="d-flex align-center" style="gap: 12px">
        <v-icon :color="firmware ? adviceColor(firmware.advice.state) : undefined">mdi-chip</v-icon>
        <div class="flex-grow-1">
          <div class="text-subtitle-1 font-weight-medium text--primary">{{ $t('firmware.title') }}</div>
          <div v-if="firmware" class="text-caption" :class="firmware.advice.state === 'update-available' ? 'primary--text' : 'text--secondary'">
            {{ firmware.currentVersion || '-' }} · {{ $t('firmware.advice.' + firmware.advice.state) }}<template v-if="lastUpdate && lastUpdate.open"> · {{ $t('firmware.engine.phases.' + lastUpdate.phase) }}</template>
          </div>
        </div>
        <v-icon>mdi-chevron-right</v-icon>
      </v-card-text>
    </v-card>

    <v-dialog v-model="open" max-width="980" scrollable>
      <v-card v-if="open">
        <v-card-title>
          {{ $t('firmware.panel.title', {name: chargingPoint.name || chargingPoint.identifier}) }}
          <v-spacer />
          <v-btn small text @click="load" :loading="loading"><v-icon small left>mdi-refresh</v-icon>{{ $t('refresh') }}</v-btn>
          <v-btn icon :aria-label="$t('close')" @click="open = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <template v-if="firmware">
            <v-alert :color="adviceColor(firmware.advice.state)" text dense class="advice">
              <div class="d-flex align-center flex-wrap" style="gap: 12px">
                <div class="flex-grow-1">
                  <div class="font-weight-medium">
                    <!-- Géré par le constructeur : il choisit ce qu'il installe (par modèle, par vague) ; on sait seulement qu'une version plus haute existe ailleurs -->
                    <template v-if="firmware.advice.state === 'update-available' && firmware.advice.target.managed">{{ $t('firmware.panel.managed-newer', {version: firmware.advice.target.version}) }}</template>
                    <template v-else-if="firmware.advice.state === 'update-available'">{{ $t('firmware.panel.update-available', {version: firmware.advice.target.version}) }}</template>
                    <template v-else-if="firmware.advice.state === 'up-to-date'">{{ $t('firmware.panel.up-to-date') }}</template>
                    <template v-else-if="firmware.advice.state === 'pinned'">{{ $t('firmware.panel.pinned', {version: firmware.pin.version}) }}</template>
                    <template v-else-if="firmware.advice.state === 'no-path' && !firmware.advice.target">{{ $t('firmware.panel.pinned-missing', {version: firmware.pin.version}) }}</template>
                    <template v-else-if="firmware.advice.state === 'no-path'">{{ $t('firmware.panel.no-path', {version: firmware.advice.target.version}) }}</template>
                    <template v-else>{{ $t('firmware.panel.not-covered') }}</template>
                  </div>
                  <div v-if="firmware.advice.target && firmware.advice.target.managed" class="text-caption">{{ $t('firmware.panel.managed-hint') }}</div>
                  <div v-if="firmware.advice.steps.length > 1" class="text-caption">{{ $t('firmware.via', {steps: firmware.advice.steps.slice(0, -1).join(' → ')}) }}</div>
                </div>
                <template v-if="canDeployFirmware">
                  <!-- Figée : on l'amène sur sa version figée directement (la page de lancement viserait la dernière). -->
                  <v-btn
                      v-if="firmware.advice.state === 'update-available' && firmware.pin"
                      small
                      color="primary"
                      :disabled="busy"
                      :loading="pinUpdating"
                      @click="updateToPin"
                  >
                    {{ $t('firmware.panel.update') }}
                  </v-btn>
                  <v-btn
                      v-else-if="firmware.advice.state === 'update-available'"
                      small
                      color="primary"
                      :disabled="busy"
                      :to="{name: 'firmware.launch', params: {chargingPointIds: [chargingPoint.id]}}"
                  >
                    {{ firmware.advice.target.managed ? $t('firmware.panel.managed-try') : $t('firmware.panel.update') }}
                  </v-btn>
                  <v-btn v-if="!firmware.pin" small outlined :disabled="busy" @click="openOther(null)">{{ $t('firmware.panel.other') }}</v-btn>
                  <!-- Après une mise à jour réussie, revenir à la version d'avant, si un chemin y mène -->
                  <v-btn v-if="!firmware.pin && rollbackTo" small outlined color="warning" :disabled="busy" @click="openOther(rollbackTo.id)">{{ $t('firmware.panel.rollback', {version: rollbackTo.name}) }}</v-btn>
                  <v-btn v-if="!firmware.pin" small outlined :disabled="busy" @click="pinDialog = true"><v-icon small left>mdi-pin-outline</v-icon>{{ $t('firmware.pin.action') }}</v-btn>
                </template>
              </div>
            </v-alert>

            <!-- Figée : pourquoi, par qui, la revue ; alerte si son firmware a été bloqué. -->
            <v-alert v-if="firmware.pin" :type="firmware.pin.blocked ? 'error' : undefined" :color="firmware.pin.blocked ? undefined : 'deep-purple'" text dense outlined class="mt-n2">
              <div class="d-flex align-center flex-wrap" style="gap: 12px">
                <div class="flex-grow-1">
                  <div class="font-weight-medium"><v-icon small color="deep-purple">mdi-pin</v-icon> {{ $t('firmware.pin.pinned-on', {version: firmware.pin.version}) }}</div>
                  <div class="text-body-2">{{ firmware.pin.reason }}</div>
                  <div class="text-caption text--secondary">
                    {{ $t('firmware.pin.by', {by: firmware.pin.pinnedBy || '-', date: formatDate(firmware.pin.pinnedAt)}) }}
                    <template v-if="firmware.pin.reviewAt"> · <span :class="{'error--text': firmware.pin.reviewDue}">{{ $t(firmware.pin.reviewDue ? 'firmware.pin.review-due' : 'firmware.pin.review-on', {date: firmware.pin.reviewAt}) }}</span></template>
                  </div>
                  <div v-if="firmware.pin.blocked" class="font-weight-medium mt-1">{{ $t('firmware.pin.blocked') }}</div>
                </div>
                <template v-if="canDeployFirmware">
                  <v-btn small text :disabled="busy" @click="pinDialog = true">{{ $t('firmware.pin.change') }}</v-btn>
                  <v-btn small outlined :disabled="busy" :loading="unpinning" @click="unpin">{{ $t('firmware.pin.unpin') }}</v-btn>
                </template>
              </div>
            </v-alert>

            <v-row dense class="mb-2">
              <v-col cols="12" sm="4">
                <div class="label">{{ $t('firmware.current-version') }}</div>
                <div class="value">{{ firmware.currentVersion || '-' }}</div>
              </v-col>
              <v-col cols="12" sm="4">
                <div class="label">{{ $t('firmware.last-update') }}</div>
                <div class="value">{{ firmware.lastUpdateAt ? formatDate(firmware.lastUpdateAt) : $t('firmware.never') }}</div>
              </v-col>
              <v-col cols="12" sm="4">
                <div class="label">{{ $t('firmware.model') }}</div>
                <div class="value">{{ [firmware.vendor, firmware.model].filter(v => v).join(' · ') || '-' }}</div>
                <div v-if="firmware.modelLabel && firmware.modelLabel !== firmware.model" class="text-caption text--secondary">{{ firmware.modelLabel }}</div>
                <router-link v-if="firmware.manufacturer && canReadFirmware" :to="{name: 'manufacturer', params: {id: firmware.manufacturer.id}}" class="text-caption">
                  {{ $t('firmware.panel.manufacturer', {name: firmware.manufacturer.name}) }}
                </router-link>
              </v-col>
            </v-row>

            <firmware-update-card
                v-if="lastUpdate"
                :update="lastUpdate"
                :charging-point-id="chargingPoint.id"
                :can-deploy="canDeployFirmware"
                :can-force="canForceFirmware"
                :target-status="statusOf(lastUpdate.targetVersionId)"
                :status-label="statusLabel"
                @changed="load"
            />

            <div v-if="previousUpdates.length" class="mb-4">
              <div class="label mb-1">{{ $t('firmware.engine.previous') }}</div>
              <div v-for="update in previousUpdates" :key="update.id" class="text-caption">
                {{ formatDate(update.createdAt) }} — {{ update.steps.map(step => step.version || '?').join(' → ') }}
                <v-chip x-small class="ml-1" :color="{succeeded: 'success', failed: 'error'}[update.phase] || 'grey'" text-color="white">{{ $t('firmware.engine.phases.' + update.phase) }}</v-chip>
                <span v-if="update.failureCode" class="text--secondary"> {{ $t('firmware.engine.failures.' + update.failureCode, {detail: update.failureDetail || ''}) }}</span>
              </div>
            </div>

            <v-data-table
                :headers="headers"
                :items="firmware.history"
                :items-per-page="10"
                :no-data-text="$t('firmware.no-history')"
                dense
            >
              <template #item.at="{item}">
                {{ formatDate(item.at, 'DD/MM/YYYY HH:mm:ss') }}
              </template>
              <template #item.kind="{item}">
                <template v-if="item.kind === 'status'">
                  <v-chip x-small :color="item.failure ? 'error' : 'grey lighten-2'" :text-color="item.failure ? 'white' : undefined">
                    {{ statusLabel(item.status) }}
                  </v-chip>
                </template>
                <template v-else>
                  {{ $t('firmware.kinds.' + item.kind) }}
                </template>
              </template>
              <template #item.detail="{item}">
                <template v-if="item.kind === 'version-changed'">
                  {{ item.fromVersion }} → <b>{{ item.toVersion }}</b>
                </template>
                <template v-else-if="item.kind === 'update-requested'">
                  <v-chip x-small :color="outcomeColor(item.outcome)" text-color="white" class="mr-2">
                    {{ $t('firmware.outcomes.' + item.outcome) }}<template v-if="item.outcome === 'succeeded' && item.toVersion"> · {{ item.toVersion }}</template>
                  </v-chip>
                  <span v-if="item.stepVersion" class="mr-1">
                    <b>{{ item.stepVersion }}</b><template v-if="item.targetVersion && item.targetVersion !== item.stepVersion"> {{ $t('firmware.towards', {version: item.targetVersion}) }}</template>
                  </span>
                  <span class="location">{{ item.location }}</span>
                  <span v-if="item.retrieveDate" class="text-caption grey--text"> · {{ $t('firmware.from-date', {date: formatDate(item.retrieveDate)}) }}</span>
                </template>
              </template>
              <template #item.requestedByName="{item}">
                {{ item.requestedByName || (item.kind === 'update-requested' ? '-' : $t('firmware.charging-point')) }}
              </template>
            </v-data-table>
          </template>

          <div v-else-if="loading" class="text-center my-6">
            <v-progress-circular indeterminate color="primary" />
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <firmware-pin-dialog
        v-if="firmware"
        v-model="pinDialog"
        :charging-point-id="chargingPoint.id"
        :current-version="firmware.currentVersion"
        :versions="firmware.pinnable"
        :pin="firmware.pin"
        @saved="load"
    />

    <firmware-other-version-dialog
        v-if="firmware"
        v-model="otherDialog"
        :charging-point-id="chargingPoint.id"
        :current-version="firmware.currentVersion"
        :versions="firmware.otherVersions"
        :initial-version-id="otherInitial"
        @sent="load"
    />
  </div>
</template>

<script>
import ChargingPointRepository from "@repository/ChargingPointRepository";
import FirmwareUpdateCard from "@pages/ChargingPoint/Detail/FirmwareUpdateCard";
import FirmwareOtherVersionDialog from "@pages/ChargingPoint/Detail/FirmwareOtherVersionDialog";
import FirmwarePinDialog from "@pages/ChargingPoint/Detail/FirmwarePinDialog";
import FirmwareRepository from "@repository/FirmwareRepository";
import FirmwareMixin from "@pages/Firmware/FirmwareMixin";
import {EventBus} from "@includes/EventBus";

/** Toutes les 10 secondes tant qu'une mise à jour est en cours : c'est le job (chaque minute) qui la fait avancer. */
const REFRESH_MS = 10000

const KNOWN_STATUSES = ['Downloading', 'Downloaded', 'DownloadFailed', 'Installing', 'Installed', 'InstallationFailed', 'Idle',
  'DownloadScheduled', 'DownloadPaused', 'InstallRebooting', 'InstallScheduled', 'InstallVerificationFailed', 'InvalidSignature', 'SignatureVerified']

/**
 * Le firmware d'une borne, dans l'onglet Gestion : un bouton qui résume sa version et où elle en est, et ouvre la fenêtre
 * (le conseil, « Mettre à jour », « Autre version… », la mise à jour en cours, les précédentes, le journal).
 */
export default {
  components: {FirmwareUpdateCard, FirmwareOtherVersionDialog, FirmwarePinDialog},
  mixins: [FirmwareMixin],

  props: {
    chargingPoint: {type: Object, required: true},
  },

  data() {
    return {
      firmware: null,
      loading: false,
      open: false,
      otherDialog: false,
      pinDialog: false,
      pinUpdating: false,
      unpinning: false,
      otherInitial: null,
      listeners: [],
      timer: null,
    }
  },

  computed: {
    // Les mises à jour du moteur, la plus récente d'abord : la carte montre la dernière, la liste les précédentes.
    lastUpdate() {
      return (this.firmware?.updates || [])[0] || null
    },
    busy() {
      return !!(this.lastUpdate && this.lastUpdate.open)
    },
    // La version d'avant la dernière mise à jour réussie, si elle est au catalogue et qu'un chemin y mène.
    rollbackTo() {
      const last = (this.firmware?.updates || [])[0]
      if (!last || last.phase !== 'succeeded' || !last.fromVersion) {
        return null
      }
      return (this.firmware.otherVersions || []).find(version => version.version === last.fromVersion && !version.problem) || null
    },
    previousUpdates() {
      return (this.firmware?.updates || []).slice(1, 6)
    },
    headers() {
      return [
        {text: this.$t('firmware.date'), value: 'at', width: 170},
        {text: this.$t('firmware.event'), value: 'kind', width: 190},
        {text: this.$t('firmware.detail'), value: 'detail', sortable: false},
        {text: this.$t('firmware.by'), value: 'requestedByName', width: 180},
      ]
    },
  },

  mounted() {
    this.load()
    this.listen()
  },

  destroyed() {
    this.listeners.forEach(token => EventBus.$off(token))
    this.schedule(false)
  },

  watch: {
    busy(value) {
      this.schedule(value)
    },
    'chargingPoint.id'() {
      this.listeners.forEach(token => EventBus.$off(token))
      this.firmware = null
      this.load()
      this.listen()
    },
  },

  methods: {
    // Le statut actuel d'un firmware visé (il a pu repasser en essai depuis le lancement) ; null : inconnu.
    statusOf(versionId) {
      const version = ((this.firmware && this.firmware.otherVersions) || []).find(other => other.id === versionId)
      return version ? version.status : null
    },

    load() {
      this.loading = true
      ChargingPointRepository.firmware(this.chargingPoint.id).then(firmware => {
        this.firmware = firmware
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.loading = false
      })
    },

    openOther(versionId) {
      this.otherInitial = versionId
      this.otherDialog = true
    },

    updateToPin() {
      this.$dialog.confirm({text: this.$t('firmware.pin.update-confirm', {version: this.firmware.advice.target.name || this.firmware.advice.target.version})}).then(result => {
        if (result) {
          this.doUpdateToPin()
        }
      })
    },

    doUpdateToPin() {
      this.pinUpdating = true
      FirmwareRepository.startUpdate(this.chargingPoint.id, this.firmware.advice.target.id).then(() => {
        this.notifySuccess('firmware.engine.retried')
        this.load()
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.pinUpdating = false
      })
    },

    unpin() {
      this.$dialog.confirm({text: this.$t('firmware.pin.unpin-confirm')}).then(result => {
        if (result) {
          this.doUnpin()
        }
      })
    },

    doUnpin() {
      this.unpinning = true
      FirmwareRepository.unpin(this.chargingPoint.id).then(() => {
        this.notifySuccess('firmware.pin.unpinned')
        this.load()
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.unpinning = false
      })
    },

    schedule(active) {
      if (active && !this.timer) {
        this.timer = setInterval(() => this.load(), REFRESH_MS)
      } else if (!active && this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },

    // Un statut ou un redémarrage de la borne rafraîchit le résumé et la fenêtre tout seuls.
    listen() {
      this.listeners = ['FirmwareStatusNotification', 'BootNotification'].map(action =>
          EventBus.$on('on-' + action + '-' + this.chargingPoint.id, () => this.load())
      )
    },

    statusLabel(status) {
      return KNOWN_STATUSES.includes(status) ? this.$t('firmware.statuses.' + status) : status
    },

    outcomeColor(outcome) {
      return {succeeded: 'success', failed: 'error', 'in-progress': 'primary'}[outcome] || 'grey'
    },
  },
}
</script>

<style scoped lang="scss">
// La fenêtre est rendue hors du composant : ses règles ne passent pas par un parent.
.trigger--attention {
  border-color: var(--v-primary-base) !important;
}
.label {
  font-size: 12px;
  color: #888;
}
.value {
  font-weight: 500;
}
.location {
  font-family: monospace;
  font-size: 12px;
  word-break: break-all;
}
</style>
